import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, GridRenderCellParams } from '@mui/x-data-grid';
import { useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import { createHospital, deleteHospital, fetchHospitals, updateHospital } from '../../store/hospitalSlice';
import { AppDispatch, RootState } from '../../store/store';
import { fetchStudies } from '../../store/studySlice';

const validationSchema = Yup.object({
  name: Yup.string().required('Name is required'),
  address: Yup.string().required('Address is required'),
});

const Hospitals: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();
  const { hospitals, loading, error } = useSelector((state: RootState) => state.hospitals);
  const { studies } = useSelector((state: RootState) => state.studies);

  const [show, setShow] = useState(false);

  useEffect(() => {
    void dispatch(fetchHospitals());
    void dispatch(fetchStudies());
  }, [dispatch]);

  const formik = useFormik({
    initialValues: {
      id: '',
      name: '',
      address: '',
      studyId: '',
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      handleSave(values);
    },
  });

  const handleClose = () => {
    setShow(false);
  };
  const handleShow = () => {
    setShow(true);
  };

  const handleSave = (values: { id: string; name: string; address: string; studyId: string }) => {
    if (values.id != '') {
      void dispatch(updateHospital(values));
    } else {
      void dispatch(createHospital(values));
    }
    handleClose();
  };

  const handleDelete = (id: string) => {
    void dispatch(deleteHospital(id));
  };

  const handleEdit = (hospital: { id: string; name: string; address: string; study: any }) => {
    void formik.setValues({ ...hospital, studyId: hospital.study?.id });
    handleShow();
  };

  const handleCreate = () => {
    formik.resetForm();
    handleShow();
  };

  const columns = [
    { field: 'id', headerName: 'ID', width: 150 },
    { field: 'study', headerName: 'Etude', width: 150, renderCell: (params: GridRenderCellParams) => params.row.study?.id || 'N/A' },
    { field: 'name', headerName: 'Nom', width: 200 },
    { field: 'address', headerName: 'Adresse', width: 300 },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 200,
      renderCell: (params: GridRenderCellParams) => (
        <Box display="flex" justifyContent="space-between" sx={{ p: 1 }} width="100%">
          <Button
            color="warning"
            onClick={() => {
              handleEdit(params.row);
            }}
            sx={{ mr: 2 }}
            variant="contained"
          >
            Modifier
          </Button>
          <Button
            color="error"
            onClick={() => {
              handleDelete(params.row.id);
            }}
            variant="contained"
          >
            Supprimer
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <Container disableGutters sx={{ flex: 1, flexDirection: 'column', p: 0 }}>
      <Typography component="h1" gutterBottom sx={{ mt: 4 }} variant="h4">
        Centres investigateurs
      </Typography>
      {loading && <CircularProgress />}

      <Button onClick={handleCreate} variant="contained">
        Créer un centre
      </Button>

      {error && (
        <Alert severity="error" sx={{ m: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mt: 4 }}>
        <DataGrid columns={columns} rows={hospitals} />
      </Box>

      <Dialog onClose={handleClose} open={show}>
        <DialogTitle>{formik.values.id != '' ? 'Editer le centre' : 'Créer un centre'}</DialogTitle>
        <DialogContent>
          <form onSubmit={formik.handleSubmit}>
            <Box mb={2} sx={{ p: 2 }}>
              <TextField
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
                id="name"
                label="Nom"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                placeholder="Entrer le nom du centre"
                type="text"
                value={formik.values.name}
              />
            </Box>
            <Box mb={2} sx={{ p: 2 }}>
              <TextField
                error={formik.touched.address && Boolean(formik.errors.address)}
                helperText={formik.touched.address && formik.errors.address}
                id="address"
                label="Addresse"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                placeholder="Enter l'adresse du centre"
                type="text"
                value={formik.values.address}
              />
            </Box>
            <Box mb={2} sx={{ p: 2 }}>
              <InputLabel>Etude</InputLabel>
              <Select fullWidth {...formik.getFieldProps('studyId')} error={formik.touched.studyId && Boolean(formik.errors.studyId)}>
                {studies.map((study) => (
                  <MenuItem key={study.id} value={study.id}>
                    {study.id}
                  </MenuItem>
                ))}
              </Select>
              {formik.touched.studyId && formik.errors.studyId && <div>{formik.errors.studyId}</div>}
            </Box>
            <DialogActions>
              <Button color="primary" onClick={handleClose}>
                Close
              </Button>
              <Button color="primary" type="submit" variant="contained">
                Save Changes
              </Button>
            </DialogActions>
          </form>
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default Hospitals;
